import { getTrustedDeviceDetails } from '@core/Areas/Login/Store/selectors'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import {
  CognitoUnauthorizedError,
  isCognitoUserAuthenticated,
} from './useCognitoUserIsAuthenticated'
import { throwApiError } from '../Helpers/utils'
import { DevicesResponse } from '@mobi/api-types'

async function fetchDevices(): Promise<DevicesResponse> {
  const isAuthenticated = await isCognitoUserAuthenticated()

  if (!isAuthenticated) {
    throw new CognitoUnauthorizedError()
  }

  try {
    const response = await fetch(`/api/mfa/get-user-devices`)
    if (!response.ok) {
      throw new Error('Failed to fetch devices')
    }
    return (await response.json()) as DevicesResponse
  } catch (error: unknown) {
    return throwApiError(error)
  }
}

export function mfaDevicesQueryKey(accountNumber: number) {
  return ['mfa-devices', accountNumber]
}

export function useMfaDevices(accountNumber: number, shouldFetch = false) {
  const storedTrustedDevices = useSelector(getTrustedDeviceDetails)
  const currentDeviceDetails = storedTrustedDevices[accountNumber]

  return useQuery({
    queryKey: mfaDevicesQueryKey(accountNumber),
    queryFn: () => fetchDevices(),
    enabled: shouldFetch,
    retry: false,
    select: (data: DevicesResponse) => {
      const trustedDevices = data.devices.filter(device => device.deviceStatus === 'valid')
      const hasCurrentDevice =
        currentDeviceDetails &&
        data.devices.some(device => device.deviceKey === currentDeviceDetails?.deviceKey)

      if (hasCurrentDevice) {
        const currentTrustedDeviceFromServer = trustedDevices.find(
          d => d.deviceKey === currentDeviceDetails.deviceKey
        )

        const deviceName = currentTrustedDeviceFromServer?.deviceName

        // if the current device is in the trusted list, then its name should have
        // 'this device' in it
        if (deviceName && !deviceName.toLowerCase().includes('this device')) {
          currentTrustedDeviceFromServer.deviceName = `${deviceName} (This Device)`
        }
      } else if (currentDeviceDetails) {
        trustedDevices.push({
          deviceName: 'This Device (Untrusted)',
          deviceKey: currentDeviceDetails?.deviceKey ?? '',
          deviceStatus: 'untrusted',
          deviceCreateDate: 0,
          deviceLastAuthenticatedDate: 0,
          deviceLastModifiedDate: 0,
        })
      }

      return { devices: trustedDevices, currentDeviceKey: currentDeviceDetails?.deviceKey }
    },
  })
}
